/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useMemo, useContext, useLayoutEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ModalBase, ModalHeader, ModalFooter, ChooseBase } from 'components';
import { useDebounce } from 'utils';
import { ManageCampaignsContext } from 'contexts';
import { useFacade } from './useFacade';
import * as S from './ChangeColumnsPopUp.styled';
import { ParentRow } from './components/parentRow/ParentRow';
import { ChildrenRow } from './components/childrenRow/ChildrenRow';
import { useSignal } from '@preact/signals-react';
import { cloneDeep } from 'lodash-es';
import { SelectedItems } from './components/selectedItems/SelectedItems';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import { immutableCols, allAdditionalFields } from 'constants';

const splitEventName = (field) => {
  const withoutPrefix = field.replace(/^events_/, '');
  const parts = withoutPrefix.split('_');
  const eventId = parts.pop();
  const eventName = parts.join('_');
  return { eventName, eventId };
};
const transformEventsList = (eventList) => {
  return eventList.map((eventData) => {
    const { eventName, eventId } = splitEventName(eventData.field);
    return {
      colId: eventData.colId,
      field: eventData.field,
      eventName: eventName,
      eventId: eventId,
      headerName: eventName,
    };
  });
};

const transformDataToSave = (newOrder, event_id_arr) => {
  const selectedCols = newOrder
    .filter((colData) => {
      if (colData.lockPosition) return false;
      if (colData?.children) return false;
      return true;
    })
    .map((colData, index) => {
      return {
        colName: colData.colId,
        label: colData.headerName,
        isSelected: !colData.hide,
        selectedOrder: colData.hide ? 0 : index + 1,
      };
    });
  const selectedEvents = newOrder
    .find((colData) => colData?.children)
    ?.children.filter(({ colId }) => colId)
    .map((eventData, index) => {
      const parts = eventData.colId.split('_');
      const colName = parts[parts.length - 1];
      const label = parts.slice(1, -1).join('_');
      const eventObj = {
        colName,
        label,
        isSelected: !eventData.hide,
        selectedOrder: eventData.hide ? 0 : index + 1,
        event_id_arr: event_id_arr,
      };
      if (!eventData.hide) {
        eventObj.selectedOrderIndex = index;
      }
      return eventObj;
    });

  const columnsObj = {
    title1: 'Available columns',
    title2: 'Selected columns',
    cols: selectedCols.filter(({ colName }) => Boolean(colName)),
  };

  const dataToSave = {
    cmId: '',
    tableName: 'campaignsReport',
    columns: JSON.stringify(columnsObj),
    events: JSON.stringify(selectedEvents ?? []),
  };
  return dataToSave;
};

const selectedEvents = (eventList = []) => {
  return eventList
    .filter(({ hide, colId }) => {
      if (!colId) return false;
      if (hide) return false;
      return true;
    })
    .map(({ colId }) => colId);
};
const initialEvents = (colDefs) => {
  const eventsObj = colDefs.find(({ headerName }) => headerName?.toLowerCase() === 'events');
  return selectedEvents(eventsObj?.children);
};
const initialColumns = (colDefs) => {
  const res = colDefs
    .filter((colsData) => {
      if (!colsData.headerName) return false;
      if (!Object.keys(colsData).length) return false;
      if (colsData.children) return false;
      if (colsData.lockPosition) return false;
      if (colsData.hide) return false;
      if (!colsData.colId) return false;
      return true;
    })
    .map(({ colId }) => colId);
  return res;
};

export const ChangeColumnsPopUp = ({ isOpen$, title }) => {
  const { campaignData$, gridRef, colDefs, setColDefs, rowData } =
    useContext(ManageCampaignsContext);
  const [saveColumnsAction, result] = manageCampaignsAPI.useColumnsHideSaveMutation();
  const searchValue$ = useSignal('');

  const event_id_arr = useMemo(() => {
    return rowData.map(({ event_id_arr }) => event_id_arr)[0];
  }, []);
  const isEventsByDefault = useMemo(() => {
    return Boolean(colDefs.find(({ children }) => children));
  }, [colDefs]);

  const groupsNames = useMemo(() => {
    let res = [{ name: 'Data', columnsList: allAdditionalFields }];
    if (isEventsByDefault) {
      res = [
        ...res,
        { name: 'Events', columnsList: transformEventsList(campaignData$.value.events) },
      ];
    }
    return res;
  }, [campaignData$.value, isEventsByDefault]);

  const fieldNameToHeaderName = useMemo(() => {
    const res = groupsNames.reduce((acc, { name, columnsList }) => {
      acc[name] = columnsList.reduce((innerAcc, { headerName, colId }) => {
        innerAcc[colId] = headerName;
        return innerAcc;
      }, {});
      return acc;
    }, {});
    return res;
  }, [groupsNames]);

  const filteredColumns$ = useSignal(groupsNames);
  const debouncedSearchValue = useDebounce(searchValue$.value, 700);

  useEffect(() => {
    const copedData = cloneDeep(groupsNames);
    if (!debouncedSearchValue) {
      filteredColumns$.value = copedData;
      return;
    }
    const res = copedData.map(({ name, columnsList }) => {
      const newColumnsList = columnsList.filter(({ headerName }) =>
        headerName.toLowerCase().includes(debouncedSearchValue.toLowerCase()),
      );
      if (newColumnsList.length && !isOpenedGroup(name)) {
        openCloseGroup(name);
      }
      if (!newColumnsList.length && isOpenedGroup(name)) {
        openCloseGroup(name);
      }
      return { name, columnsList: newColumnsList };
    });
    filteredColumns$.value = res;
  }, [debouncedSearchValue]);

  const {
    isActive: isOpenedGroup,
    toggle: openCloseGroup,
    toggleOn: toggleGroupOn,
    toggleOff: toggleGroupOff,
  } = useFacade();

  const draggableFlag = useRef(false);
  const columns = useFacade(initialColumns(colDefs));
  const events = useFacade(initialEvents(colDefs));

  const changeColumnsApi = useMemo(() => {
    let res = { Data: columns };
    if (isEventsByDefault) {
      res.Events = events;
    }
    return res;
  }, [isEventsByDefault, columns, events]);

  const isGroupChecked = useCallback(
    (columns, name) => columns?.map(({ field }) => field).every(changeColumnsApi[name].isActive),
    [],
  );
  const isSomeInGroupChecked = useCallback(
    (apps, name) => apps.map(({ field }) => field).some(changeColumnsApi[name].isActive),
    [],
  );

  const handleToggleGroup = (columnsList, name) => {
    const fieldsInGroup = columnsList.map(({ field }) => field);
    if (fieldsInGroup.every(changeColumnsApi[name].isActive)) {
      return changeColumnsApi[name].toggle(...fieldsInGroup);
    }
    changeColumnsApi[name].toggle(
      ...fieldsInGroup.filter((field) => !changeColumnsApi[name].isActive(field)),
    );
  };

  useLayoutEffect(() => {
    return groupsNames.forEach(({ name, columnsList }) => {
      const somethingInGroupSelected = isSomeInGroupChecked(columnsList, name);
      const action = somethingInGroupSelected ? toggleGroupOn : toggleGroupOff;
      action(name);
    });
  }, [campaignData$.value.selectedColsArray]);

  const changeColumnsAction = async (columnsOrder, eventsOrder = []) => {
    // const currentColumnState = gridRef.current.api.getColumnState();
    // if (eventsOrder.length === 0) {
    //   return;
    // }
    const visibleColumns = [];
    columnsOrder.forEach((colId) => {
      const column = colDefs.find((column) => column.colId === colId);
      if (column) {
        visibleColumns.push({ ...column, hide: false });
      }
    });
    const hiddenColumns = colDefs
      .filter(
        (colData) => (colData.colId && !columnsOrder.includes(colData.colId)) || colData.children,
      )
      .map((colData) => ({ ...colData, hide: true }));
    let orderToUpdate = [...visibleColumns, ...hiddenColumns];
    if (eventsOrder.length) {
      orderToUpdate = orderToUpdate.map((colData) => {
        const eventsList = colData.children;
        if (eventsList) {
          const groupEventsOrder =
            colData.headerName === 'Events (Cohort)'
              ? eventsOrder.map((colId) => colId.replace(/^events_/, 'eventsCohort_'))
              : eventsOrder;
          let visibleEvents = [];
          groupEventsOrder.forEach((colId) => {
            const visibleEvent = eventsList.find((column) => column.colId === colId);
            if (visibleEvent && !visibleEvents.some((eventData) => eventData.colId === colId)) {
              visibleEvents.push({ ...visibleEvent, hide: false });
            }
          });
          let hiddenEvents = eventsList
            .filter((eventData) => {
              if (!eventData.colId) return false;
              return !groupEventsOrder.includes(eventData.colId);
            })
            .map((eventData) => ({ ...eventData, hide: true }));
          return {
            ...colData,
            children: [...visibleEvents, ...hiddenEvents, { flex: 1 }],
            hide: visibleEvents.length > 0 ? false : true,
          };
        }
        return colData;
      });
    } else {
      orderToUpdate = orderToUpdate
        .map((colData) => {
          if (colData.children) {
            const newChildren = colData.children.map((eventData) => ({ ...eventData, hide: true }));
            return { ...colData, children: newChildren };
          }
          return colData;
        })
        .concat([{ flex: 1 }]);
    }

    const res = await saveColumnsAction(transformDataToSave(orderToUpdate, event_id_arr));
    if (res.data.status === 'ok') {
      setColDefs(orderToUpdate);
      isOpen$.value = false;
    }

    // gridRef.current.api.applyColumnState({
    //   state: [...newColumnState],
    //   applyOrder: true,
    // });
  };

  const saveButtonDisabled = useMemo(() => {
    if (isEventsByDefault) {
      return (
        events.state.length === 0 ||
        events.changesFlag.current ||
        columns.changesFlag.current ||
        draggableFlag.current
      );
    } else {
      return columns.changesFlag.current || draggableFlag.current;
    }
  }, [
    isEventsByDefault,
    events?.state?.length,
    events.changesFlag.current,
    columns.changesFlag.current,
    draggableFlag.current,
  ]);

  return (
    <ModalBase>
      <div>
        <ModalHeader
          title={title}
          close={() => {
            isOpen$.value = false;
          }}
        />
        <div style={{ display: 'flex', width: 601 }}>
          <S.Box>
            <ChooseBase.Divided
              title='Search'
              searchValue={searchValue$.value}
              searchOnChange={(e) => (searchValue$.value = e.target.value)}
              handleClearSearchValue={() => (searchValue$.value = '')}
            >
              <div>
                {filteredColumns$.value.map(({ name, columnsList }) => (
                  <div key={name} style={{ display: 'flex', flexDirection: 'column' }}>
                    <ParentRow
                      handleToggleGroup={handleToggleGroup}
                      isOpenedGroup={isOpenedGroup}
                      openCloseGroup={openCloseGroup}
                      isGroupChecked={isGroupChecked}
                      name={name}
                      id={`${name}-row`}
                      columnsList={columnsList}
                    />
                    {isOpenedGroup(name) &&
                      columnsList.map(({ headerName, field, colId }, index) => {
                        const isSelected = changeColumnsApi[name].isActive;
                        const toggleSelectedItem = changeColumnsApi[name].toggle;
                        return (
                          <ChildrenRow
                            isSelected={isSelected}
                            key={colId}
                            label={headerName}
                            id={field}
                            withShadow={!index}
                            toggleSelectedItem={toggleSelectedItem}
                            searchValue$={searchValue$}
                          />
                        );
                      })}
                  </div>
                ))}
              </div>
            </ChooseBase.Divided>
          </S.Box>
          <SelectedItems
            draggableFlag={draggableFlag}
            changeColumnsApi={changeColumnsApi}
            fieldNameToHeaderName={fieldNameToHeaderName}
          />
        </div>
        <ModalFooter
          saveButtonText='Save Columns'
          saveButtonOnClick={() => {
            console.log('Save Columns list');
            changeColumnsAction(
              [...immutableCols, ...changeColumnsApi.Data.state],
              changeColumnsApi?.Events?.state,
            );
          }}
          close={() => {
            isOpen$.value = false;
          }}
          saveButtonDisabled={!saveButtonDisabled}
        />
      </div>
    </ModalBase>
  );
};
